import { Component, EventEmitter, Input, Output, ViewChild, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, FormsModule } from '@angular/forms';
import { MatDatepickerModule, MatDatepicker, MatDateRangePicker, MatDateRangeInput } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { CustomDateAdapter, CUSTOM_DATE_FORMATS } from './date-adapter';
import { DateRangeValue, getLastNumDays, getThisMonth, getLastMonth, getRelativeDate, subtractDays } from '../../../utils/time';
import { AuthorizationService } from '../../../services/authorization.service';

export enum FixedDateRanges {
  YESTERDAY = "Yesterday",
  LAST_7_DAYS = "Last 7 Days",
  LAST_14_DAYS = "Last 14 Days",
  LAST_30_DAYS = "Last 30 Days",
  THIS_MONTH = "This Month",
  LAST_MONTH = "Last Month",
}

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatDateRangePicker,
    MatDateRangeInput,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatTooltipModule,
    AssetVersionPipe
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
  ],
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss']
})
export class DatePickerComponent implements OnInit {
  @Input() range!: FormGroup<{
    startDate: FormControl<Date | null>;
    endDate: FormControl<Date | null>;
    relativeDate: FormControl<FixedDateRanges | undefined>;
  }>;
  private authorizationService = inject(AuthorizationService);

  @Output() dateRangeSelected = new EventEmitter<DateRangeValue | null>();
  @ViewChild('picker') picker!: MatDatepicker<Date>;
  protected selectedRangeValue: FixedDateRanges | string = '';
  protected isCustomRange!: boolean;
  protected readonly fixedDateRanges = FixedDateRanges;
  protected readonly fixedDateRangesValues = Object.values(FixedDateRanges);
  protected readonly maxDate = new Date(new Date().setDate(new Date().getDate() - 1));
  protected readonly minDate = this.getMinDate();
  protected readonly isTrialUser = this.authorizationService.isTrialUser;

  constructor() {
  }

  private getMinDate(): Date {
    if (this.authorizationService.isTrialUser) {
      return subtractDays(new Date(), 30);
    }
    return new Date(2025, 6, 1);
  }

  ngOnInit() {
    this.selectedRangeValue = this.getDateRangeDisplay(new Date(this.range.value.startDate as Date), new Date(this.range.value.endDate as Date));
  }

  public selectPresetDays(days: number): void {
    this.updateRangeAndEmit({ ...getLastNumDays(days), relativeDate: getRelativeDate(days) });
    this.picker.close();
  }

  public selectThisMonth(): void {
    this.updateRangeAndEmit(getThisMonth());
    this.picker.close();
  }

  public selectLastMonth(): void {
    this.updateRangeAndEmit(getLastMonth());
    this.picker.close();
  }


  public onApply(): void {
    const rangeValue = this.range.value;
    if (rangeValue.startDate) {
      const dateRange: DateRangeValue = {
        startDate: new Date(rangeValue.startDate),
        endDate: new Date(rangeValue.endDate || rangeValue.startDate)
      };
      this.updateRangeAndEmit(dateRange);
    }
  }

  public onCancel(): void {
    this.picker.close();
  }

  private updateRangeAndEmit(dateRange: DateRangeValue): void {
    this.range.patchValue(dateRange);
    const selectedRangeValue = this.getDateRangeDisplay(dateRange.startDate as Date, dateRange.endDate as Date);
    this.selectedRangeValue = selectedRangeValue;
    if (!this.isCustomRange) {
      dateRange.relativeDate = selectedRangeValue as FixedDateRanges;
    }
    this.dateRangeSelected.emit(dateRange);
  }

  private isSameDateRange(dateRange: DateRangeValue, start: Date, end: Date): boolean {
    return this.isSameDate(dateRange.startDate as Date, start) && this.isSameDate(dateRange.endDate as Date, end)
  }

  private isSameDate(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  }

  private getDateRangeDisplay(start: Date, end: Date): FixedDateRanges | string {
    const yesterday = getLastNumDays(1);
    if (this.isSameDateRange(yesterday, start, end)) {
      this.isCustomRange = false;
      return FixedDateRanges.YESTERDAY;
    }
    const last7Days = getLastNumDays(7);
    if (this.isSameDateRange(last7Days, start, end)) {
      this.isCustomRange = false;
      return FixedDateRanges.LAST_7_DAYS;
    }
    const last14Days = getLastNumDays(14);
    if (this.isSameDateRange(last14Days, start, end)) {
      this.isCustomRange = false;
      return FixedDateRanges.LAST_14_DAYS;
    }
    const last30Days = getLastNumDays(30);
    if (this.isSameDateRange(last30Days, start, end)) {
      this.isCustomRange = false;
      return FixedDateRanges.LAST_30_DAYS;
    }
    const thisMonth = getThisMonth();
    if (this.isSameDateRange(thisMonth, start, end)) {
      this.isCustomRange = false;
      return FixedDateRanges.THIS_MONTH;
    }
    const lastMonth = getLastMonth();
    if (this.isSameDateRange(lastMonth, start, end)) {
      this.isCustomRange = false;
      return FixedDateRanges.LAST_MONTH;
    }
    this.isCustomRange = true;
    return this.formatDateRange(start, end);
  }


  private formatDateRange(start: Date, end: Date): string {
    if (this.isSameDate(start, end)) {
      return this.formatFullDate(start);
    }
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();
    if (startYear === endYear) {
      return this.formatSameYearDate(start, end);
    } else {
      return `${this.formatFullDate(start)} - ${this.formatFullDate(end)}`;
    }
  }

  private formatFullDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  }

  private formatSameYearDate(start: Date, end: Date): string {
    const formatStartDate = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    }).format(start);
    const formatEndDate = this.formatFullDate(end);
    return `${formatStartDate} - ${formatEndDate}`
  }
}